import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  LineChart, 
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useBusiness } from '../contexts/BusinessContext';
import AdminApiService from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { ErrorBoundary } from 'react-error-boundary';

// Sample colors for charts - updated to match Tailwind theme
const COLORS = ['#00C6FF', '#FF5722', '#FFBB28', '#FF8042', '#8884d8'];

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="text-center my-8">
      <h2 className="text-xl font-semibold mb-2 text-red-500">Something went wrong</h2>
      <p className="text-gray-400 mb-6">{error.message}</p>
      <button 
        className="btn-primary"
        onClick={resetErrorBoundary}
      >
        Try again
      </button>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Get everything we need from contexts
  const { 
    selectedBusiness, 
    businesses, 
    loading: businessLoading, 
    error: businessError, 
    refreshBusinesses 
  } = useBusiness();
  const { isPlatformAdmin } = useAuth();
  
  const [timeframe, setTimeframe] = useState('week');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  
  // Determine if there are any businesses
  const noBusinessesExist = businesses.length === 0;

  // Handle retrying business data loading
  const handleRetryBusinessLoad = useCallback(() => {
    refreshBusinesses().catch(err => {
      console.error('Error retrying business fetch:', err);
    });
  }, [refreshBusinesses]);

  // Modify the useEffect hook for dashboard data
  useEffect(() => {
    console.log("Dashboard states:", selectedBusiness, fetchAttempted, timeframe, location.pathname);
    
    // Handle platform admin on dashboard path
    if (isPlatformAdmin && location.pathname === '/dashboard') {
      // For platform admins on the main dashboard, we'll show platform-wide metrics
      // but we no longer clear the selected business - this was causing the navigation to break
      
      // Make sure we're not stuck in loading state
      setLoading(false);
    }
    // For business-specific dashboard (either platform admin viewing a business or regular user)
    else if (selectedBusiness && !fetchAttempted) {
      fetchDashboardData();
    }
    // If no business is selected and we're still loading, stop loading
    else if (!selectedBusiness && loading) {
      setLoading(false);
    }
  }, [selectedBusiness, timeframe, fetchAttempted, isPlatformAdmin, location.pathname, loading]);

  // Update the fetchDashboardData function
  const fetchDashboardData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      setError(null);
      setFetchAttempted(true); // Mark that we've attempted
      
      const data = await AdminApiService.analytics.getDashboardStats(selectedBusiness.id);
      setDashboardData(data);
      
      // DO NOT reset fetchAttempted to false here - this was causing the infinite loop
      // We only want to refetch when the timeframe changes or manual retry is requested
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError('Failed to load dashboard data. Please try again later.');
      // Keep fetchAttempted as true to prevent retry loop
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  const handleTimeframeChange = (event) => {
    // Set new timeframe and reset fetch attempt to trigger a new fetch
    setTimeframe(event.target.value);
    setFetchAttempted(false);
  };

  // Loading state
  if (businessLoading || loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Handle business error if present
  if (businessError) {
    return (
      <div className="text-center my-8">
        <h2 className="text-xl font-semibold mb-2 text-red-500">Business Error</h2>
        <p className="text-gray-400 mb-6">{businessError}</p>
        <button 
          className="btn-primary"
          onClick={handleRetryBusinessLoad}
        >
          Retry Loading Businesses
        </button>
      </div>
    );
  }

  // Handle dashboard error
  if (error) {
    return (
      <div className="text-center my-8">
        <h2 className="text-xl font-semibold mb-2 text-red-500">Dashboard Error</h2>
        <p className="text-gray-400 mb-6">{error}</p>
        <button 
          className="btn-primary"
          onClick={() => setFetchAttempted(false)}
        >
          Retry Loading Dashboard
        </button>
      </div>
    );
  }

  // Show platform-wide dashboard for platform admins on the dashboard page
  // If not platform admin and no business selected, prompt to select a business
  if (isPlatformAdmin && location.pathname === '/dashboard') {
    // For platform admins on dashboard page, show platform-wide dashboard with aggregate statistics
    // Generate some sample data for the charts
      const platformOrderData = [
        { name: 'Mon', orders: 14 },
        { name: 'Tue', orders: 21 },
        { name: 'Wed', orders: 25 },
        { name: 'Thu', orders: 18 },
        { name: 'Fri', orders: 29 },
        { name: 'Sat', orders: 35 },
        { name: 'Sun', orders: 22 },
      ];
      
      const platformRevenueData = [
        { name: 'Mon', revenue: 145 },
        { name: 'Tue', revenue: 210 },
        { name: 'Wed', revenue: 250 },
        { name: 'Thu', revenue: 180 },
        { name: 'Fri', revenue: 290 },
        { name: 'Sat', revenue: 350 },
        { name: 'Sun', revenue: 220 },
      ];
      
      const platformOrderTypeData = [
        { name: 'Voice', value: 65 },
        { name: 'Text', value: 35 }
      ];
      
      const platformBusinessData = [
        { name: 'Restaurants', value: 45 },
        { name: 'Retail', value: 25 },
        { name: 'Services', value: 30 }
      ];
      
      return (
        <div className="pb-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-white">
              Platform Dashboard
            </h1>
            <div className="relative">
              <select
                id="timeframe-select"
                value={timeframe}
                onChange={handleTimeframeChange}
                className="form-input pl-3 pr-10 py-2 text-sm w-40"
              >
                <option value="day">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
            </div>
          </div>
          
          {/* Platform-wide summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="card p-4">
              <p className="text-gray-400 text-sm mb-1">
                Total Businesses
              </p>
              <p className="text-2xl font-semibold">
                {businesses.length}
              </p>
              <p className="text-sm text-green-500">
                +12% from last period
              </p>
            </div>
            
            <div className="card p-4">
              <p className="text-gray-400 text-sm mb-1">
                Active Businesses
              </p>
              <p className="text-2xl font-semibold">
                {businesses.filter(b => b.status === 'active').length}
              </p>
              <p className="text-sm text-green-500">
                +8% from last period
              </p>
            </div>
            
            <div className="card p-4">
              <p className="text-gray-400 text-sm mb-1">
                Total Orders
              </p>
              <p className="text-2xl font-semibold">
                164
              </p>
              <p className="text-sm text-green-500">
                +24% from last period
              </p>
            </div>
            
            <div className="card p-4">
              <p className="text-gray-400 text-sm mb-1">
                Total Revenue
              </p>
              <p className="text-2xl font-semibold">
                $1,645
              </p>
              <p className="text-sm text-green-500">
                +18% from last period
              </p>
            </div>
          </div>
          
          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-8">
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Platform Orders</h3>
                </div>
                <div className="p-4">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={platformOrderData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" />
                        <XAxis dataKey="name" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1E1E1E', 
                            borderColor: '#2D2D2D',
                            color: '#FFF'
                          }} 
                        />
                        <Legend />
                        <Bar dataKey="orders" fill="#00C6FF" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <div className="card h-full">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Order Types</h3>
                </div>
                <div className="p-4">
                  <div className="h-[300px] flex justify-center items-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={platformOrderTypeData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {platformOrderTypeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1E1E1E', 
                            borderColor: '#2D2D2D',
                            color: '#FFF'
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Second row of charts */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-8">
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Platform Revenue</h3>
                </div>
                <div className="p-4">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={platformRevenueData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" />
                        <XAxis dataKey="name" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip 
                          formatter={(value) => ['$' + value, 'Revenue']} 
                          contentStyle={{ 
                            backgroundColor: '#1E1E1E', 
                            borderColor: '#2D2D2D',
                            color: '#FFF'
                          }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#00C6FF" activeDot={{ r: 8 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <div className="card h-full">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Business Types</h3>
                </div>
                <div className="p-4">
                  <div className="h-[300px] flex justify-center items-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={platformBusinessData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {platformBusinessData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1E1E1E', 
                            borderColor: '#2D2D2D',
                            color: '#FFF'
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Business list card */}
          <div className="card">
            <div className="p-4 border-b border-dark-600">
              <h3 className="text-lg font-medium">Recent Businesses</h3>
            </div>
            <div className="p-4">
              {businesses.length === 0 ? (
                <p className="text-gray-400 text-center py-4">
                  No businesses available. Create your first business to get started.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Subdomain</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Status</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-600">
                      {businesses.slice(0, 5).map(business => (
                        <tr key={business.id}>
                          <td className="px-4 py-3 whitespace-nowrap">{business.name}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{business.subdomain}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              business.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'
                            }`}>
                              {business.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <Link 
                              to={`/businesses/${business.id}`}
                              className="text-primary-400 hover:text-primary-300"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {businesses.length > 5 && (
                    <div className="mt-4 text-center">
                      <Link to="/businesses" className="text-primary-400 hover:text-primary-300">
                        View all businesses
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
  }
  
  // Handle case when no business is selected for non-platform admins
  if (!selectedBusiness && !isPlatformAdmin) {
    // For non-platform admins without a selected business
    return (
      <div className="text-center my-8">
        <h2 className="text-xl font-semibold mb-2">
          {noBusinessesExist 
            ? 'No Businesses Available' 
            : 'No Business Selected'}
        </h2>
        <p className="text-gray-400 mb-6">
          {noBusinessesExist
            ? 'You do not have access to any businesses. Please contact the platform administrator.'
            : 'Please select a business to view its dashboard.'}
        </p>
        
        <Link 
          to="/businesses"
          className="btn-primary"
        >
          View My Businesses
        </Link>
      </div>
    );
  }

  // Check if we have real data
  const hasRealData = dashboardData && 
    (dashboardData.recentOrders?.data?.length > 0 || 
     dashboardData.revenue?.data?.length > 0 || 
     dashboardData.popularItems?.length > 0);
     
  // Use empty data if we don't have real data yet
  const emptyOrderData = [
    { name: 'Mon', orders: 0 },
    { name: 'Tue', orders: 0 },
    { name: 'Wed', orders: 0 },
    { name: 'Thu', orders: 0 },
    { name: 'Fri', orders: 0 },
    { name: 'Sat', orders: 0 },
    { name: 'Sun', orders: 0 },
  ];
  
  const emptyRevenueData = [
    { name: 'Mon', revenue: 0 },
    { name: 'Tue', revenue: 0 },
    { name: 'Wed', revenue: 0 },
    { name: 'Thu', revenue: 0 },
    { name: 'Fri', revenue: 0 },
    { name: 'Sat', revenue: 0 },
    { name: 'Sun', revenue: 0 },
  ];
  
  // Determine which data to use
  const orderData = hasRealData && dashboardData.recentOrders?.data?.length > 0 
    ? dashboardData.recentOrders.data.map(d => ({ name: d.name || 'Day', orders: d.orders || 0 }))
    : emptyOrderData;
    
  const revenueData = hasRealData && dashboardData.revenue?.data?.length > 0 
    ? dashboardData.revenue.data.map(d => ({ name: d.name || 'Day', revenue: d.revenue || 0 }))
    : emptyRevenueData;

  return (
    <div className="pb-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-white">
          Dashboard
        </h1>
        
        <div className="relative">
          <select
            id="timeframe-select"
            value={timeframe}
            onChange={handleTimeframeChange}
            className="form-input pl-3 pr-10 py-2 text-sm w-40"
          >
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <p className="text-gray-400 text-sm mb-1">
            Total Orders
          </p>
          <p className="text-2xl font-semibold">
            {dashboardData?.orderStats?.totalOrders || 0}
          </p>
          <p className="text-sm text-gray-400">
            No previous data for comparison
          </p>
        </div>
        
        <div className="card p-4">
          <p className="text-gray-400 text-sm mb-1">
            Revenue
          </p>
          <p className="text-2xl font-semibold">
            ${dashboardData?.revenueStats?.totalRevenue || 0}
          </p>
          <p className="text-sm text-gray-400">
            No previous data for comparison
          </p>
        </div>
        
        <div className="card p-4">
          <p className="text-gray-400 text-sm mb-1">
            Voice Orders
          </p>
          <p className="text-2xl font-semibold">
            {dashboardData?.voiceStats?.totalCalls || 0}
          </p>
          <p className="text-sm text-gray-400">
            No previous data for comparison
          </p>
        </div>
        
        <div className="card p-4">
          <p className="text-gray-400 text-sm mb-1">
            Chat Orders
          </p>
          <p className="text-2xl font-semibold">
            {dashboardData?.chatStats?.totalChats || 0}
          </p>
          <p className="text-sm text-gray-400">
            No previous data for comparison
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-dark-600">
        <div className="flex">
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 0 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(0)}
          >
            Orders
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 1 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(1)}
          >
            Revenue
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 2 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(2)}
          >
            AI Usage
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className={tabValue !== 0 ? 'hidden' : ''}>
        {tabValue === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Orders Over Time</h3>
                </div>
                <div className="p-4">
                  {!hasRealData ? (
                    <div className="h-[300px] flex flex-col justify-center items-center">
                      <div className="text-center text-gray-400 p-6 rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-lg font-medium mb-2">No Orders Yet</p>
                        <p className="text-sm">Order history will appear here when orders are placed.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={orderData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" />
                          <XAxis dataKey="name" stroke="#9CA3AF" />
                          <YAxis stroke="#9CA3AF" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1E1E1E', 
                              borderColor: '#2D2D2D',
                              color: '#FFF'
                            }} 
                          />
                          <Legend />
                          <Bar dataKey="orders" fill="#00C6FF" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <div className="card h-full">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Order Types</h3>
                </div>
                <div className="p-4">
                  {!hasRealData ? (
                    <div className="h-[300px] flex flex-col justify-center items-center">
                      <div className="text-center text-gray-400 p-6 rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-lg font-medium mb-2">No Order Data Yet</p>
                        <p className="text-sm">Order type distribution will appear here when orders are placed.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-[300px] flex justify-center items-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={dashboardData.orderTypes}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {dashboardData.orderTypes && dashboardData.orderTypes.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1E1E1E', 
                              borderColor: '#2D2D2D',
                              color: '#FFF'
                            }}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-12">
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Recent Orders</h3>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-400">
                    No recent orders to display.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={tabValue !== 1 ? 'hidden' : ''}>
        {tabValue === 1 && (
          <div className="grid grid-cols-1 gap-4">
            <div className="card">
              <div className="p-4 border-b border-dark-600">
                <h3 className="text-lg font-medium">Revenue Over Time</h3>
              </div>
              <div className="p-4">
                {!hasRealData ? (
                  <div className="h-[400px] flex flex-col justify-center items-center">
                    <div className="text-center text-gray-400 p-6 rounded-md">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-lg font-medium mb-2">No Revenue Data Yet</p>
                      <p className="text-sm">Revenue trends will appear here when orders are placed.</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={revenueData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" />
                        <XAxis dataKey="name" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip 
                          formatter={(value) => ['$' + value, 'Revenue']} 
                          contentStyle={{ 
                            backgroundColor: '#1E1E1E', 
                            borderColor: '#2D2D2D',
                            color: '#FFF'
                          }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#00C6FF" activeDot={{ r: 8 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={tabValue !== 2 ? 'hidden' : ''}>
        {tabValue === 2 && (
          <div className="grid grid-cols-1 gap-4">
            <div className="card">
              <div className="p-4 border-b border-dark-600">
                <h3 className="text-lg font-medium">AI Usage Metrics</h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-400">
                  AI usage metrics will be displayed here when data is available.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wrap the component with ErrorBoundary before exporting
export default function DashboardPageWithErrorBoundary() {
  return (
    <ErrorBoundary 
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Reset application state here if needed
        window.location.reload();
      }}
    >
      <DashboardPage />
    </ErrorBoundary>
  );
}